import { useState, useMemo } from "react";
import { useParams } from "react-router";
import { Button, InputNumber, Input, Select, Popconfirm, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import type { BudgetLine, QuantitySource } from "./types";
import { activeSites } from "./plannedSites";

const CATEGORIES = [
  "Site Costs",
  "Per Patient",
  "Monitoring",
  "CRO Fees",
  "Data Management",
  "Regulatory",
  "Medical Writing",
  "Drug Supply",
  "Contingency",
  "Other",
];

const SOURCE_OPTIONS: { value: QuantitySource; label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "sites", label: "# Sites" },
  { value: "enrolled", label: "# Enrolled" },
  { value: "screened", label: "# Screened" },
];

const SOURCE_BADGE: Record<QuantitySource, { label: string; color: string }> = {
  manual: { label: "", color: "" },
  sites: { label: "sites", color: "bg-[#D8EEF8] text-[#196A9F]" },
  enrolled: { label: "enrolled", color: "bg-[#E0DEF0] text-[#59528F]" },
  screened: { label: "screened", color: "bg-[#CFE5D7] text-[#486563]" },
};

function usd(n: number): string {
  return n === 0
    ? "—"
    : `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function CategoryBadge({ category }: { category: string }) {
  const colors: Record<string, string> = {
    "Site Costs": "bg-[#D8EEF8] text-[#196A9F]",
    "Per Patient": "bg-[#E0DEF0] text-[#59528F]",
    Monitoring: "bg-[#F8EEE1] text-[#C06119]",
    "CRO Fees": "bg-[#E7D2DF] text-[#8C2760]",
    "Data Management": "bg-[#CFE5D7] text-[#486563]",
    Regulatory: "bg-[#F8EEE1] text-[#994C1D]",
    "Medical Writing": "bg-[#D8EEF8] text-[#316E97]",
    "Drug Supply": "bg-[#E7D2DF] text-[#691D49]",
    Contingency: "bg-[#F3F4F6] text-[#6B7280]",
    Other: "bg-[#F3F4F6] text-[#6B7280]",
  };
  return (
    <span
      className={`inline-flex items-center h-5 rounded-[4px] px-1.5 text-[12px] font-semibold ${colors[category] ?? "bg-[#F3F4F6] text-[#6B7280]"}`}
    >
      {category}
    </span>
  );
}

const EMPTY_LINE: Omit<BudgetLine, "id"> = {
  category: "Site Costs",
  lineItem: "",
  unit: "",
  quantitySource: "manual",
  quantity: 0,
  unitCost: 0,
  notes: "",
};

function AddLineForm({
  onAdd,
  onCancel,
}: {
  onAdd: (line: Omit<BudgetLine, "id">) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Omit<BudgetLine, "id">>(EMPTY_LINE);
  const set = <K extends keyof typeof draft>(k: K, v: (typeof draft)[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <tr className="bg-[#F0FBFF]">
      <td className="px-3 py-2">
        <Select
          size="small"
          value={draft.category}
          style={{ width: 138 }}
          options={CATEGORIES.map((c) => ({ value: c, label: c }))}
          onChange={(v) => set("category", v)}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          size="small"
          placeholder="Line item description"
          value={draft.lineItem}
          onChange={(e) => set("lineItem", e.target.value)}
          style={{ width: 220 }}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          size="small"
          placeholder="e.g. per site"
          value={draft.unit}
          onChange={(e) => set("unit", e.target.value)}
          style={{ width: 110 }}
        />
      </td>
      <td className="px-3 py-2">
        <Select
          size="small"
          value={draft.quantitySource}
          style={{ width: 110 }}
          options={SOURCE_OPTIONS}
          onChange={(v) => set("quantitySource", v)}
        />
      </td>
      <td className="px-3 py-2">
        {draft.quantitySource === "manual" ? (
          <InputNumber
            size="small"
            min={0}
            value={draft.quantity}
            onChange={(v) => set("quantity", v ?? 0)}
            style={{ width: 80 }}
          />
        ) : (
          <span className="text-[12px] text-[#6B7280] italic">auto</span>
        )}
      </td>
      <td className="px-3 py-2">
        <InputNumber
          size="small"
          min={0}
          prefix="$"
          value={draft.unitCost}
          onChange={(v) => set("unitCost", v ?? 0)}
          style={{ width: 110 }}
        />
      </td>
      <td className="px-3 py-2">
        <span className="text-[12px] text-[#6B7280]">—</span>
      </td>
      <td className="px-3 py-2">
        <Input
          size="small"
          placeholder="Notes"
          value={draft.notes}
          onChange={(e) => set("notes", e.target.value)}
          style={{ width: 140 }}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <Button
            size="small"
            type="primary"
            className="bg-[#0C98D0] border-none"
            disabled={!draft.lineItem.trim()}
            onClick={() => {
              onAdd(draft);
            }}
          >
            Add
          </Button>
          <Button size="small" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function TrialPlanningBudget() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  const [addingLine, setAddingLine] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>("All");

  // Derived quantities from plan data (computed before early return so useMemo calls are unconditional)
  // Excluded (rejected) sites do not drive per-site quantities.
  const numSites = activeSites(plan?.sites ?? []).length;
  const targetEnrollment = plan?.targetEnrollment ?? 0;
  const sfr = plan?.screenFailureRate ?? 20;
  const patientsToScreen =
    targetEnrollment > 0 ? Math.ceil(targetEnrollment / (1 - sfr / 100)) : 0;

  const derivedQty = (source: QuantitySource, manualQty: number): number => {
    switch (source) {
      case "sites":
        return numSites;
      case "enrolled":
        return targetEnrollment;
      case "screened":
        return patientsToScreen;
      default:
        return manualQty;
    }
  };

  // Migrate old lines missing quantitySource
  const lines: BudgetLine[] = (plan?.budget ?? []).map((l) => ({
    ...l,
    quantitySource: (l.quantitySource ?? "manual") as QuantitySource,
  }));

  const grandTotal = useMemo(
    () =>
      lines.reduce(
        (sum, l) => sum + derivedQty(l.quantitySource, l.quantity) * l.unitCost,
        0,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
    [lines, numSites, targetEnrollment, patientsToScreen],
  );

  const categoryTotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of lines) {
      const sub = derivedQty(l.quantitySource, l.quantity) * l.unitCost;
      map.set(l.category, (map.get(l.category) ?? 0) + sub);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, [lines, numSites, targetEnrollment, patientsToScreen]);

  if (!plan) return null;

  const save = (b: BudgetLine[]) => updatePlan(plan.id, { budget: b });
  const update = (id: string, changes: Partial<BudgetLine>) =>
    save(lines.map((l) => (l.id === id ? { ...l, ...changes } : l)));
  const remove = (id: string) => save(lines.filter((l) => l.id !== id));
  const addLine = (line: Omit<BudgetLine, "id">) => {
    save([...lines, { ...line, id: crypto.randomUUID() }]);
    setAddingLine(false);
  };

  const usedCategories = [...new Set(lines.map((l) => l.category))];
  const filteredLines =
    filterCategory === "All"
      ? lines
      : lines.filter((l) => l.category === filterCategory);

  return (
    <div className="flex flex-col h-full">
      {/* Context strip */}
      <div className="flex gap-3 mb-4 flex-wrap shrink-0 items-center">
        {numSites > 0 && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-[#D8EEF8] border border-[#7ABBDD] px-3 py-1.5">
            <LinkOutlined className="text-[#196A9F] text-[11px]" />
            <span className="text-[12px] text-[#196A9F]">
              Sites: <strong>{numSites}</strong>
            </span>
          </div>
        )}
        {targetEnrollment > 0 && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-[#E0DEF0] border border-[#B3ADCF] px-3 py-1.5">
            <LinkOutlined className="text-[#59528F] text-[11px]" />
            <span className="text-[12px] text-[#59528F]">
              Enrolled: <strong>{targetEnrollment}</strong>
            </span>
          </div>
        )}
        {patientsToScreen > 0 && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-[#CFE5D7] border border-[#8CBF9C] px-3 py-1.5">
            <LinkOutlined className="text-[#486563] text-[11px]" />
            <span className="text-[12px] text-[#486563]">
              Screened: <strong>{patientsToScreen}</strong>
            </span>
          </div>
        )}
        <div className="ml-auto text-[12px] text-[#6B7280]">
          Quantities marked with a link icon are auto-calculated from plan data
          above.
        </div>
        {grandTotal > 0 && (
          <div className="flex items-center gap-1.5 rounded-[6px] bg-[#1F2933] px-3 py-1.5">
            <span className="text-[12px] text-white/60">Total estimate:</span>
            <span className="text-[13px] font-bold text-white">
              {usd(grandTotal)}
            </span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-3 shrink-0">
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          className="bg-[#0C98D0] border-none"
          onClick={() => setAddingLine(true)}
        >
          Add line item
        </Button>
        {usedCategories.length > 1 && (
          <Select
            size="small"
            value={filterCategory}
            style={{ width: 165 }}
            options={[
              { value: "All", label: "All categories" },
              ...usedCategories.map((c) => ({ value: c, label: c })),
            ]}
            onChange={setFilterCategory}
          />
        )}
        {grandTotal > 0 && usedCategories.length > 1 && (
          <div className="ml-auto flex gap-3 flex-wrap">
            {usedCategories
              .filter((cat) => (categoryTotals.get(cat) ?? 0) > 0)
              .map((cat) => (
                <span key={cat} className="text-[12px] text-[#6B7280]">
                  {cat}:{" "}
                  <strong className="text-[#1F2933]">
                    {usd(categoryTotals.get(cat) ?? 0)}
                  </strong>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 rounded-[8px] border border-[#F0F2F4] bg-white overflow-hidden">
        {lines.length === 0 && !addingLine ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-[14px] text-[#6B7280] text-center px-8 max-w-md">
              Your budget template is loading. Create a new plan to get a
              pre-populated budget with site costs, per-patient fees, CRO,
              regulatory, and more.
            </p>
            <Button
              size="small"
              type="primary"
              className="mt-3 bg-[#0C98D0] border-none"
              icon={<PlusOutlined />}
              onClick={() => setAddingLine(true)}
            >
              Add first line item
            </Button>
          </div>
        ) : (
          <div className="overflow-auto h-full">
            <table className="w-full text-left" style={{ minWidth: 960 }}>
              <thead>
                <tr className="border-b border-[#F0F2F4] bg-[#FAFAFA]">
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    Category
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    Line Item
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    Unit
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    <Tooltip title="Auto-link quantity to plan data: # sites, # enrolled, or # screened">
                      <span className="underline decoration-dotted cursor-help">
                        Qty source
                      </span>
                    </Tooltip>
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    Quantity
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    Unit Cost
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap text-right">
                    Subtotal
                  </th>
                  <th className="px-3 py-2 text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
                    Notes
                  </th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {filteredLines.map((l) => {
                  const effQty = derivedQty(l.quantitySource, l.quantity);
                  const subtotal = effQty * l.unitCost;
                  const badge = SOURCE_BADGE[l.quantitySource];
                  return (
                    <tr
                      key={l.id}
                      className="border-b border-[#F9FAFB] hover:bg-[#FAFAFA]"
                    >
                      <td className="px-3 py-1.5">
                        <Select
                          size="small"
                          value={l.category}
                          style={{ width: 138 }}
                          options={CATEGORIES.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                          onChange={(v) => update(l.id, { category: v })}
                          labelRender={({ value }) => (
                            <CategoryBadge category={value as string} />
                          )}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          size="small"
                          value={l.lineItem}
                          onChange={(e) =>
                            update(l.id, { lineItem: e.target.value })
                          }
                          style={{ fontSize: 12, width: 200 }}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          size="small"
                          value={l.unit}
                          placeholder="per site, flat…"
                          onChange={(e) =>
                            update(l.id, { unit: e.target.value })
                          }
                          style={{ fontSize: 12, width: 110 }}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Select
                          size="small"
                          value={l.quantitySource}
                          style={{ width: 110 }}
                          options={SOURCE_OPTIONS}
                          onChange={(v) => update(l.id, { quantitySource: v })}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        {l.quantitySource === "manual" ? (
                          <InputNumber
                            size="small"
                            min={0}
                            value={l.quantity}
                            onChange={(v) => update(l.id, { quantity: v ?? 0 })}
                            style={{ width: 80 }}
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[13px] font-semibold text-[#1F2933]">
                              {effQty}
                            </span>
                            <span
                              className={`inline-flex items-center h-5 rounded-[3px] px-1 text-[12px] font-semibold ${badge.color}`}
                            >
                              <LinkOutlined className="mr-0.5 text-[9px]" />
                              {badge.label}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <InputNumber
                          size="small"
                          min={0}
                          prefix="$"
                          value={l.unitCost}
                          onChange={(v) => update(l.id, { unitCost: v ?? 0 })}
                          style={{ width: 110 }}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <span
                          className={`text-[12px] font-medium ${subtotal > 0 ? "text-[#1F2933]" : "text-[#D1D5DB]"}`}
                        >
                          {usd(subtotal)}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          size="small"
                          value={l.notes}
                          placeholder="Note"
                          onChange={(e) =>
                            update(l.id, { notes: e.target.value })
                          }
                          style={{ fontSize: 12, width: 140 }}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Popconfirm
                          title="Remove?"
                          okText="Remove"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => remove(l.id)}
                        >
                          <Button
                            size="small"
                            type="text"
                            icon={<DeleteOutlined />}
                            className="text-[#9CA3AF] hover:text-[#EF4444]"
                          />
                        </Popconfirm>
                      </td>
                    </tr>
                  );
                })}
                {addingLine && (
                  <AddLineForm
                    onAdd={addLine}
                    onCancel={() => setAddingLine(false)}
                  />
                )}
                {grandTotal > 0 && (
                  <tr className="bg-[#1F2933]">
                    <td
                      colSpan={6}
                      className="px-3 py-2 text-[12px] font-semibold text-white/60 text-right"
                    >
                      Total estimate
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="text-[14px] font-bold text-white">
                        {usd(grandTotal)}
                      </span>
                    </td>
                    <td colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
